import React from "react"
import PropTypes from "prop-types"
import { withRadium, DetectCompact } from "higher-order-components"
import { childParser } from "containers/utilities"

/**
 * @param {Boolean} [fullWidth=false] - True sets columns to full width. This overrides maxWidth.
 * @param {Boolean} [gutter] - Adds gutter spacing to all columns.
 * @param {Boolean} [divider] - Adds divider between each column.
 * @param {Number} [maxWidth=1200] - Maximum width of column group (px).
 * @param {Boolean} [verticalPadding] - Adds padding before and after module.
 * @param {String} [backgroundColor] - Hex code for background color.
 * @param {String} [textColor] - Hex code for text color.
 * @param {Array|Column} components - Array of Column components.
 * @returns {ReactElement} React Component
 */
const ColumnGroup = ({
  components,
  children,
  compact,
  backgroundColor,
  textColor,
  verticalPadding,
  horizontalPadding = true,
  fullWidth,
  maxWidth = 1200,
  gutter,
  divider,
  style
} = {}) => {
  const columns = !compact
  const childrenCount = React.Children.count(children) || components.length
  return (
    <div
      data-test="column-group"
      style={[
        backgroundColor && { background: backgroundColor },
        textColor && { color: textColor },
        { width: "100%" },
        { ...style }
      ]}>
      <div
        style={[
          styles.base,
          styles.padding({
            verticalPadding,
            horizontalPadding: !fullWidth && horizontalPadding
          }),
          columns ? styles.columns : styles.row,
          fullWidth ? "" : styles.maxWidth(maxWidth)
        ]}>
        {childParser({ components, children }, index => ({
          gutter,
          count: childrenCount,
          divider: divider && index < childrenCount - 1
        }))}
      </div>
    </div>
  )
}

ColumnGroup.propTypes = {
  children: PropTypes.node,
  maxWidth: PropTypes.number,
  fullWidth: PropTypes.bool,
  components: PropTypes.array,
  compact: PropTypes.bool,
  gutter: PropTypes.bool,
  verticalPadding: PropTypes.bool,
  horizontalPadding: PropTypes.bool,
  divider: PropTypes.bool,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string
}

export default withRadium(DetectCompact({
  threshold: 820
}))(ColumnGroup)

const styles = {
  base: {
    boxSizing: "border-box",
    display: "flex",
    flex: "1 1 auto",
    width: "100%"
  },
  padding: ({ verticalPadding, horizontalPadding }) => ({
    padding: `${(verticalPadding && 5) || 0}em ${(horizontalPadding && 5) || 0}vw`
  }),
  columns: {
    flexWrap: "wrap"
  },
  row: {
    flexDirection: "column"
  },
  maxWidth: number => ({
    maxWidth: `calc(${number}px + ${2 * 5}vw)`,
    margin: "0 auto"
  })
}



// WEBPACK FOOTER //
// ./src/containers/column-group/index.jsx